import React, { useEffect } from 'react';
import { Drawer, Backdrop, IconButton, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import tokens from '../../assets/theme/tokens';
import CloseIcon from '@material-ui/icons/Close';
import { MDTypography } from '../MDTypography';

const useStyles = makeStyles((theme) => ({
  drawer: {
    zIndex: 1300,
    position: 'relative',
  },
  drawerPaper: {
    position: 'fixed',
    top: 0,
    height: '100vh',
    border: 'none',
    boxShadow: tokens.shadows.lg,
    borderLeft: `1px solid ${tokens.colors.border}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  backdrop: {
    zIndex: 1250,
    // A slate veil, lighter than the old black one, as steerage's overlays.
    backgroundColor: 'rgba(15, 23, 42, 0.35)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: tokens.colors.text,
    minHeight: 64,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(3),
    // Content cards sit on the slate page colour, as in the steerage app.
    backgroundColor: tokens.colors.bg,
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
}));

type ContextPanelSize = 'small' | 'medium' | 'large' | 'fullsize';
type ContextPanelSide = 'left' | 'right';

interface ContextPanelProps {
  open: boolean;
  onClose: () => void;
  side?: ContextPanelSide;
  size?: ContextPanelSize;
  title?: React.ReactNode;
  children: React.ReactNode;
}

const getSizeWidth = (size: ContextPanelSize): string => {
  switch (size) {
    case 'small':
      return '25%';
    case 'medium':
      return '50%';
    case 'large':
      return '75%';
    case 'fullsize':
      return '95%';
    default:
      return '50%';
  }
};

export const ContextPanel: React.FC<ContextPanelProps> = ({
  open,
  onClose,
  side = 'right',
  size = 'medium',
  title,
  children,
}) => {
  const classes = useStyles();
  const width = getSizeWidth(size);

  // Handle escape key press
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [open, onClose]);

  // Handle click outside
  const handleBackdropClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClose();
  };

  return (
    <>
      <Backdrop
        open={open}
        onClick={handleBackdropClick}
        className={classes.backdrop}
      />
      <Drawer
        anchor={side}
        open={open}
        onClose={onClose}
        variant="persistent"
        className={classes.drawer}
        PaperProps={{
          className: classes.drawerPaper,
          style: {
            width,
            [side]: 0,
          },
        }}
        ModalProps={{
          keepMounted: true,
          disablePortal: false,
          hideBackdrop: true, // We use our own backdrop
        }}
      >
        {title && (
          <Box className={classes.header}>
            {typeof title === 'string' ? (
              <MDTypography variant="h6" fontWeight="medium">
                {title}
              </MDTypography>
            ) : (
              title
            )}
            <IconButton
              onClick={onClose}
              className={classes.closeButton}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <Box className={classes.content}>{children}</Box>
      </Drawer>
    </>
  );
};
